/**
 * AssuranceRuleSummary - the rule read back as a sentence.
 *
 * Mode, direction and four numbers combine into something a reader has to
 * assemble in their head. One line stating what earns High and what falls to
 * None is the cheapest check that the panel says what the person meant, and it
 * is the only part of the tab that spells the rule out in words.
 *
 * A per-period or rolling base has no single figure to quote, so the sentence
 * stays in percentage terms and names where the base comes from instead.
 */
import { Fragment } from 'react';
import type { BaseValueType, BoundaryDirection, DataValueType, Level, LevelCalculationMode } from '../api/types';
import { getBandAxis } from '../utils/assuranceBands';
import { parseThresholdInput } from '../utils/validation';

interface AssuranceRuleSummaryProps {
  thresholds: Record<Level, string>;
  mode: LevelCalculationMode;
  direction: BoundaryDirection;
  dataValueType: DataValueType;
  baseValueType: BaseValueType;
  /** The single base value, when one applies. Null for per-period and rolling bases. */
  fixedBaseValue: number | null;
  rollingAverageMonths: number | null;
}

function Figure({ children }: { children: React.ReactNode }) {
  return <span className="font-mono font-medium">{children}</span>;
}

function formatBase(value: number): string {
  return value.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

/**
 * `High` plus an optional `, which is …` suffix. Returned as an array so JSX
 * does not insert a space before the comma.
 */
function highLabel(example: React.ReactNode | null): React.ReactNode {
  return [
    <strong key="high" className="font-semibold text-content-default">
      High
    </strong>,
    example == null ? null : <Fragment key="worked">, which is {example}</Fragment>
  ];
}

export default function AssuranceRuleSummary({
  thresholds,
  mode,
  direction,
  dataValueType,
  baseValueType,
  fixedBaseValue,
  rollingAverageMonths
}: AssuranceRuleSummaryProps) {
  const axis = getBandAxis({ thresholds, mode, direction, dataValueType });
  const high = parseThresholdInput(thresholds.high);
  const limited = parseThresholdInput(thresholds.limited);

  if (high === null || limited === null) {
    return null;
  }

  const highText = thresholds.high.trim();
  const limitedText = thresholds.limited.trim();
  const rawUnit = dataValueType === 'percentage' ? '%' : '';

  let body: React.ReactNode;

  if (mode === 'absolute') {
    body = (
      <>
        A value of{' '}
        <Figure>
          {axis.ascending ? '≤' : '≥'} {highText}
          {axis.unit}
        </Figure>{' '}
        is <strong className="font-semibold text-content-default">High</strong>. Anything{' '}
        {axis.ascending ? 'over' : 'under'}{' '}
        <Figure>
          {limitedText}
          {axis.unit}
        </Figure>{' '}
        is <strong className="font-semibold text-content-default">None</strong>.
      </>
    );
  } else {
    // Only a fixed base resolves to real figures. The others vary by period,
    // so name where the base comes from rather than inventing one.
    const baseSource =
      baseValueType === 'monthly'
        ? "that period's base"
        : baseValueType === 'rolling_average'
          ? `the average of the previous ${rollingAverageMonths ?? 3} periods`
          : 'the base';

    const worked = baseValueType === 'fixed' && fixedBaseValue !== null && fixedBaseValue !== 0 ? fixedBaseValue : null;

    if (mode === 'deviation_bands') {
      body = (
        <>
          A value within <Figure>±{highText}%</Figure> of {baseSource} is{' '}
          {highLabel(
            worked === null ? null : (
              <>
                <Figure>
                  {formatBase(worked * (1 - high / 100))}
                  {rawUnit}
                </Figure>{' '}
                to{' '}
                <Figure>
                  {formatBase(worked * (1 + high / 100))}
                  {rawUnit}
                </Figure>
              </>
            )
          )}
          . Past <Figure>±{limitedText}%</Figure> is{' '}
          <strong className="font-semibold text-content-default">None</strong>.
        </>
      );
    } else {
      body = (
        <>
          A value at{' '}
          <Figure>
            {axis.ascending ? '≤' : '≥'} {highText}%
          </Figure>{' '}
          of {baseSource} is{' '}
          {highLabel(
            worked === null ? null : (
              <>
                <Figure>
                  {formatBase((worked * high) / 100)}
                  {rawUnit}
                </Figure>{' '}
                or {axis.ascending ? 'less' : 'more'}
              </>
            )
          )}
          . {axis.ascending ? 'Over' : 'Under'} <Figure>{limitedText}%</Figure> is{' '}
          <strong className="font-semibold text-content-default">None</strong>.
        </>
      );
    }
  }

  return (
    <p className="rounded-r-md border-l-[3px] border-border-active bg-background-hover px-3 py-2.5 text-sm text-content-subtle">
      {body}
    </p>
  );
}
